import {
  Accordion,
  AccordionContent,
  AccordionItem,
  CheckboxAccordionTrigger,
} from "@/components/ui/accordion";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton.tsx";
import { FilterHeader } from "@/components/ui/molecules/FilterHeader.tsx";
import { RankedListItem } from "@/components/ui/molecules/RankedListItem";
import { ResizablePanel } from "@/components/ui/resizable";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { useShareVoiceSidebarSubscription } from "@/data-access/share-of-voice/subscriptions/useShareOfVoiceFilterSubscriptions";
import { getPrettyName } from "@/lib/utils/prettyName";
import { fetchSideBarFilters } from "@/store/share-of-voice/filters/shareOfVoice-filters-async.actions";
import { useGroupedSegments } from "@/store/share-of-voice/filters/shareOfVoice-filters.selectors";
import useShareOfVoiceSidebarFilters from "@/store/share-of-voice/filters/shareOfVoice-filters.slice";
import { FunnelIcon } from "@phosphor-icons/react";
import { useEffect, useMemo } from "react";

interface ShareOfVoiceFilterContainerProps {
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>;
  toggleFilterSelection: (
    category: ShareVoiceSidebarCategory,
    item: string | number,
  ) => void;
}

const ShareOfVoiceFilterContainer = ({
  selectedFilters,
  toggleFilterSelection,
}: ShareOfVoiceFilterContainerProps) => {
  const { isFetchingSidebarFilters } = useShareOfVoiceSidebarFilters();
  const allGroupedSegments = useGroupedSegments();

  const categoryCheckedState = useMemo(() => {
    const checkedState: Record<string, boolean> = {};

    for (const category of Object.keys(allGroupedSegments)) {
      const categoryType = allGroupedSegments[category]?.[0]?.category;
      checkedState[category] = categoryType
        ? Boolean(selectedFilters.get(categoryType)?.includes(category))
        : false;
    }

    return checkedState;
  }, [allGroupedSegments, selectedFilters]);

  useEffect(() => {
    void fetchSideBarFilters();
  }, []);

  useShareVoiceSidebarSubscription({
    enabled: !isFetchingSidebarFilters,
  });

  if (isFetchingSidebarFilters) {
    return <ItemSkeleton />;
  }

  const handleCategoryToggle = (category: string) => {
    const categoryType = allGroupedSegments[category]?.[0]?.category;

    if (categoryType) {
      toggleFilterSelection(categoryType, category);
    }
  };

  return (
    <ResizablePanel defaultSize={35} minSize={30}>
      <div className="flex h-full w-full flex-col">
        <FilterHeader icon={<FunnelIcon />} label="Filters" />
        <div className="overflow-y-scroll h-full scrollbar-thin scrollbar-thumb-gray-150">
          <div className={"px-2"}>
            <Accordion type={"multiple"}>
              {Object.keys(allGroupedSegments).map((category) => {
                return (
                  <AccordionItem value={category} key={category}>
                    <CheckboxAccordionTrigger
                      checkboxId={category}
                      checkboxProps={{
                        checked: categoryCheckedState[category],
                        onCheckedChange: () => handleCategoryToggle(category),
                      }}
                    >
                      {getPrettyName(category)}
                    </CheckboxAccordionTrigger>
                    <AccordionContent>
                      {allGroupedSegments[category]?.map((item, idx) => (
                        <RankedListItem
                          rank={idx + 1}
                          color={item.segmentColor}
                          label={item.segmentName}
                          count={item.total}
                          percentage={item.percentage}
                          key={item.segmentId}
                          isLoading={item.isLoadingCountAndPercentage}
                        />
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </div>
    </ResizablePanel>
  );
};

export default ShareOfVoiceFilterContainer;
